import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { PhoneCall, Infinity as InfinityIcon, Plus } from 'lucide-react';
import { rememberCallOrigin, clearCallOrigin } from '../lib/paymentReturn';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shows how many consultations the patient has left, with a top-up link when
 * the balance is empty. `remaining === -1` means an unlimited plan.
 */
export const CallBalanceBadge = ({ className = '' }) => {
  const { t } = useLanguage();
  const [state, setState] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/subscriptions/my-subscription`, { withCredentials: true })
      .then((r) => { if (alive) setState(r.data); })
      .catch(() => { if (alive) setState({ active: false }); });
    return () => { alive = false; };
  }, []);

  if (!state) return null;

  const remaining = state.active ? state.consultations_remaining : 0;
  const unlimited = remaining === -1;
  const empty = !state.active || remaining === 0;

  return (
    <div
      className={`inline-flex items-center gap-3 rounded-2xl border px-4 py-2.5 ${empty ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-100'} ${className}`}
      data-testid="call-balance-badge"
    >
      <span className={`w-9 h-9 rounded-full flex items-center justify-center ${empty ? 'bg-amber-500' : 'bg-[#1e88e5]'} text-white flex-shrink-0`}>
        {unlimited ? <InfinityIcon size={17} /> : <PhoneCall size={16} />}
      </span>
      <div className="leading-tight">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          {t('رصيد المكالمات', 'Call balance')}
        </p>
        <p
          className={`text-sm font-bold ${empty ? 'text-amber-800' : 'text-[#0d47a1]'}`}
          data-testid="call-balance-value"
        >
          {unlimited
            ? t('مكالمات غير محدودة', 'Unlimited consultations')
            : empty
              ? t('لا يوجد رصيد', 'No calls left')
              : t(`${remaining} مكالمة متبقية`, `${remaining} consultation(s) left`)}
        </p>
      </div>
      {empty && (
        <Link
          to="/subscriptions"
          onClick={() => {
            // Only the doctors/pharmacy list is a meaningful return target.
            if (window.location.pathname.startsWith('/doctors')) rememberCallOrigin();
            else clearCallOrigin();
          }}
          className="ms-1 inline-flex items-center gap-1 rounded-full bg-[#1e88e5] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#1565c0] transition-colors"
          data-testid="call-balance-topup-link"
        >
          <Plus size={13} />
          {t('شراء مكالمة', 'Buy a call')}
        </Link>
      )}
    </div>
  );
};

export default CallBalanceBadge;
